import { Box, BoxProps, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import useRolePermissions from '../../../hooks/useRolePermissions/useRolePermissions';
import usePortalConfig from '../../../hooks/usePortalConfig/usePortalConfig';

import { SecurityIcon2x } from '../../../components/Icons/SecurityIcon';
import { ChevronRightIcon2x } from '../../../components/Icons/ChevronRightIcon';
import ListIcon from '../../../components/Icons/ListIcon';
import UnassignedDevicesIcon from '../../../components/Icons/UnassignedDevicesIcon';

const CardContainer = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem',
  borderRadius: '0.75rem',
  boxShadow: '0px 5px 12px 0px rgba(0, 0, 0, 0.15)',
}));

const IconContainer = styled(Box)<BoxProps>(({ theme }) => ({
  padding: '0.75rem',
  borderRadius: '50%',
  backgroundColor: theme.palette.monochrome.monoChrome2,
  flexShrink: 0,
}));

const TextContainer = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: '1rem',
  justifyContent: 'center',
}));

const LinkChevronContainer = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginLeft: 'auto',
}));

function DashboardLinks() {
  const [t] = useTranslation();
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();

  return (
    <Grid container columnSpacing="2rem" sx={{ paddingBottom: '2rem' }}>
      <Grid
        item
        xl={isUserAllowed(routePermissions.unassignedDeviceList) ? 4 : 6}
        md={6}
        xs={12}
        sx={{ paddingBottom: '2rem' }}
      >
        <CardContainer>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <IconContainer>
              <SecurityIcon2x />
            </IconContainer>
            <TextContainer>
              <Typography sx={{ fontWeight: 'bold', lineHeight: 'normal' }}>
                {t('translation:pages.dashboard.links.auditTrail')}
              </Typography>
              <Typography sx={{ lineHeight: 'normal' }}>
                {t('translation:pages.dashboard.links.auditDescription')}
              </Typography>
            </TextContainer>
            <LinkChevronContainer>
              <Link to="/dashboard/auditTrail">
                <ChevronRightIcon2x />
              </Link>
            </LinkChevronContainer>
          </Box>
        </CardContainer>
      </Grid>

      <Grid
        item
        xl={isUserAllowed(routePermissions.unassignedDeviceList) ? 4 : 6}
        md={6}
        xs={12}
        sx={{ paddingBottom: '2rem' }}
      >
        <CardContainer>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <IconContainer>
              <ListIcon />
            </IconContainer>
            <TextContainer>
              <Typography sx={{ fontWeight: 'bold', lineHeight: 'normal' }}>
                {t('translation:pages.dashboard.links.devices')}
              </Typography>
              <Typography sx={{ lineHeight: 'normal' }}>
                {t('translation:pages.dashboard.links.devicesDescription')}
              </Typography>
            </TextContainer>
            <LinkChevronContainer>
              <Link to="/dashboard/devicesReport">
                <ChevronRightIcon2x />
              </Link>
            </LinkChevronContainer>
          </Box>
        </CardContainer>
      </Grid>

      {isUserAllowed(routePermissions.unassignedDeviceList) ? (
        <Grid item xl={4} md={6} xs={12} sx={{ paddingBottom: '2rem' }}>
          <CardContainer>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <IconContainer>
                <UnassignedDevicesIcon />
              </IconContainer>
              <TextContainer>
                <Typography sx={{ fontWeight: 'bold', lineHeight: 'normal' }}>
                  {t('translation:pages.dashboard.links.unassignedDevices')}
                </Typography>
                <Typography sx={{ lineHeight: 'normal' }}>
                  {t('translation:pages.dashboard.links.unassignedDescription')}
                </Typography>
              </TextContainer>
              <LinkChevronContainer>
                <Link to="/dashboard/unassignedReport">
                  <ChevronRightIcon2x />
                </Link>
              </LinkChevronContainer>
            </Box>
          </CardContainer>
        </Grid>
      ) : null}
    </Grid>
  );
}

export default DashboardLinks;
